import {
  CheckCircle2,
  Loader2,
  MessageSquare,
  Mic,
  Pause,
  Play,
  Square,
  Trash2,
} from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createPageRecording,
  DEFAULT_PAGE_RECORDING_TITLE,
  getPageRecordingUploadUrl,
  MAX_PAGE_RECORDING_SIZE_BYTES,
  PageRecordingApiError,
  validatePageRecordingBlob,
  uploadPageRecordingBlob,
} from '@/apis/page-recordings';
import { Button } from '@/components/ui/button';
import { showErrorToast, showSuccessToast } from '@/components/ui/CustomToast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMediaQuery, mediaQueries } from '@/hooks/useMediaQuery';
import { usePageClientContext } from '@/hooks/usePageClientContext';
import { useRecordingContext, type RecordingContext } from '@/hooks/useRecordingContext';
import { cn } from '@/lib/utils';

const formatDuration = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

type RecorderPhase = 'idle' | 'recording' | 'paused' | 'submitting' | 'confirmed';
const AUDIO_BITS_PER_SECOND = 64000;
type GlobalVoiceRecorderProps = {
  showVoiceRecorder?: boolean;
  showChatButton?: boolean;
  renderTrigger?: (control: {
    visible: boolean;
    onClick: () => void;
    disabled: boolean;
    active: boolean;
    label: string;
    icon: ReactNode;
    status?: string;
  }) => ReactNode;
};

export const GlobalVoiceRecorder = ({
  showVoiceRecorder = true,
  showChatButton = true,
  renderTrigger,
}: GlobalVoiceRecorderProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageClient = usePageClientContext();
  const originRef = useRef<{ context: RecordingContext; route: string; name: string } | null>(null);
  const [switchClientOpen, setSwitchClientOpen] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const startingRef = useRef(false);
  const lastCheckedClientRef = useRef<string | undefined>();

  const { recordingContext } = useRecordingContext();
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const segmentsRef = useRef<Blob[]>([]);
  const draftAudioUrlRef = useRef<string | null>(null);
  const [phase, setPhase] = useState<RecorderPhase>('idle');
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [draftBlob, setDraftBlob] = useState<Blob | null>(null);
  const [draftAudioUrl, setDraftAudioUrl] = useState('');
  const [submissionStatusText, setSubmissionStatusText] = useState('Submitting...');
  const [isDraftReviewOpen, setIsDraftReviewOpen] = useState(false);
  const [isWaveTickHigh, setIsWaveTickHigh] = useState(false);
  const isMobile = useMediaQuery(mediaQueries.maxLg);

  const canRecord = useMemo(
    () => Boolean(recordingContext.page_key && recordingContext.page_key !== 'unknown'),
    [recordingContext.page_key]
  );

  useEffect(() => {
    if (phase !== 'recording') {
      return;
    }
    const intervalId = window.setInterval(() => {
      setDurationSeconds((previousValue) => previousValue + 1);
      setIsWaveTickHigh((previousValue) => !previousValue);
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [phase]);

  const revokeDraftAudioUrl = useCallback(() => {
    if (draftAudioUrlRef.current) {
      URL.revokeObjectURL(draftAudioUrlRef.current);
      draftAudioUrlRef.current = null;
    }
  }, []);

  const replaceDraftAudioUrl = useCallback(
    (nextBlob: Blob) => {
      revokeDraftAudioUrl();
      const nextAudioUrl = URL.createObjectURL(nextBlob);
      draftAudioUrlRef.current = nextAudioUrl;
      setDraftAudioUrl(nextAudioUrl);
    },
    [revokeDraftAudioUrl]
  );

  useEffect(
    () => () => {
      revokeDraftAudioUrl();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    },
    [revokeDraftAudioUrl]
  );

  const resetDraftState = useCallback(() => {
    originRef.current = null;
    lastCheckedClientRef.current = undefined;
    setSwitchClientOpen(false);
    segmentsRef.current = [];
    setDraftBlob(null);
    setDurationSeconds(0);
    setSubmissionStatusText('Submitting...');
    revokeDraftAudioUrl();
    setDraftAudioUrl('');
  }, [revokeDraftAudioUrl]);

  const discardDraft = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsDraftReviewOpen(false);
    setPhase('idle');
    resetDraftState();
  }, [resetDraftState]);

  const startRecordingSegment = useCallback(async () => {
    if (startingRef.current) return;
    if (!canRecord && !originRef.current) {
      showErrorToast('Voice note unavailable', 'Page context is missing for this route.');
      return;
    }
    startingRef.current = true;
    setIsStarting(true);
    if (!originRef.current) {
      originRef.current = {
        context: { ...recordingContext, metadata: { ...recordingContext.metadata } },
        route: location.pathname + location.search,
        name:
          pageClient.pathname === location.pathname && pageClient.clientName
            ? pageClient.clientName
            : recordingContext.title || 'Voice note',
      };
      lastCheckedClientRef.current = recordingContext.client;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const segmentBlob = new Blob(chunksRef.current, {
          type: mediaRecorder.mimeType || 'audio/webm',
        });
        if (segmentBlob.size > 0) {
          const nextSegments = [...segmentsRef.current, segmentBlob];
          segmentsRef.current = nextSegments;
          const mergedBlob = new Blob(nextSegments, { type: segmentBlob.type || 'audio/webm' });
          replaceDraftAudioUrl(mergedBlob);
          setDraftBlob(mergedBlob);
        }
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        setPhase('paused');
        setIsDraftReviewOpen(true);
      };

      mediaRecorder.start(250);
      setIsDraftReviewOpen(false);
      setPhase('recording');
    } catch {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (!segmentsRef.current.length) originRef.current = null;
      showErrorToast('Microphone error', 'Permission denied or microphone not available.');
    } finally {
      startingRef.current = false;
      setIsStarting(false);
    }
  }, [
    canRecord,
    replaceDraftAudioUrl,
    recordingContext,
    location.pathname,
    location.search,
    pageClient,
  ]);

  const pauseRecording = useCallback(() => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') {
      return;
    }
    mediaRecorderRef.current.pause();
    setPhase('paused');
  }, []);

  const resumeRecording = useCallback(() => {
    if (!mediaRecorderRef.current) {
      startRecordingSegment();
      return;
    }
    if (mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setPhase('recording');
      return;
    }
    if (mediaRecorderRef.current.state === 'inactive') {
      startRecordingSegment();
    }
  }, [startRecordingSegment]);

  const finishRecording = useCallback(() => {
    if (!mediaRecorderRef.current) {
      return;
    }
    if (
      mediaRecorderRef.current.state === 'recording' ||
      mediaRecorderRef.current.state === 'paused'
    ) {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const routeClient = location.pathname.match(
    /\/(?:client(?:\/[^/]+)?|member\/[^/]+)\/([0-9a-f-]{36})(?:\/|$)/i
  )?.[1];
  const currentClient = routeClient ?? recordingContext.client;
  useEffect(() => {
    if (!originRef.current || !['recording', 'paused'].includes(phase)) return;
    const destination = currentClient;
    if (destination === originRef.current.context.client) {
      lastCheckedClientRef.current = destination;
      return;
    }
    if (
      !destination ||
      destination === originRef.current.context.client ||
      destination === lastCheckedClientRef.current
    )
      return;
    lastCheckedClientRef.current = destination;
    pauseRecording();
    setIsDraftReviewOpen(false);
    setSwitchClientOpen(true);
  }, [currentClient, phase, pauseRecording]);

  useEffect(() => {
    if (!['recording', 'paused', 'submitting'].includes(phase)) return;
    const protectDraft = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', protectDraft);
    return () => window.removeEventListener('beforeunload', protectDraft);
  }, [phase]);

  const submitDraft = useCallback(async () => {
    if (phase === 'submitting') {
      return;
    }
    if (!draftBlob) {
      showErrorToast('No draft found', 'Record audio before submitting.');
      return;
    }
    if (draftBlob.size > MAX_PAGE_RECORDING_SIZE_BYTES) {
      showErrorToast('Recording too large', 'Please keep voice notes under 200MB.');
      return;
    }
    setPhase('submitting');
    try {
      setSubmissionStatusText('Validating audio...');
      await validatePageRecordingBlob(draftBlob);
      const normalizedContentType = draftBlob.type || 'audio/webm';
      const fileName = `voice-note-${Date.now()}.webm`;
      setSubmissionStatusText('Preparing upload...');
      const uploadMeta = await getPageRecordingUploadUrl(
        fileName,
        normalizedContentType,
        draftBlob.size
      );
      setSubmissionStatusText('Uploading audio...');
      await uploadPageRecordingBlob(uploadMeta.upload_url, draftBlob, uploadMeta.upload_headers);

      setSubmissionStatusText('Saving recording...');
      const savedContext = originRef.current?.context ?? recordingContext;
      await createPageRecording({
        s3_file: uploadMeta.file_id,
        client: savedContext.client,
        page_key: savedContext.page_key,
        context_type: savedContext.context_type ?? '',
        context_id: savedContext.context_id ?? '',
        title: savedContext.title ?? DEFAULT_PAGE_RECORDING_TITLE,
        duration_seconds: durationSeconds,
        recorded_at: new Date().toISOString(),
        consent_captured: true,
        is_shared_with_client: false,
        auto_transcribe: true,
        metadata: savedContext.metadata ?? {},
      });

      setIsDraftReviewOpen(false);
      showSuccessToast('Recording submitted', 'Voice note uploaded successfully.');
      setPhase('confirmed');
      setTimeout(() => {
        setPhase('idle');
        resetDraftState();
      }, 1200);
    } catch (error) {
      const errorMessage =
        error instanceof PageRecordingApiError
          ? error.message
          : 'Could not submit this recording. Please retry.';
      showErrorToast('Submission failed', errorMessage);
      setPhase('paused');
    }
  }, [draftBlob, durationSeconds, phase, recordingContext, resetDraftState]);

  const isRecording = phase === 'recording';
  const isPaused = phase === 'paused';
  const isSubmitting = phase === 'submitting';
  const isConfirmed = phase === 'confirmed';
  const showActionPanel = isRecording || isPaused || isSubmitting || isConfirmed;
  const statusText = isRecording
    ? 'Recording'
    : isPaused
      ? 'Paused'
      : isSubmitting
        ? 'Uploading'
        : isConfirmed
          ? 'Saved'
          : isStarting
            ? 'Starting…'
            : 'Voice note';
  const openChat = useCallback(() => {
    navigate('/chat');
  }, [navigate]);

  return (
    <>
      {showActionPanel && (
        <div
          className="fixed z-50 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-popover px-3 py-2 text-popover-foreground shadow-md"
          style={
            isMobile
              ? { left: 8, right: 8, top: 'calc(52px + env(safe-area-inset-top))' }
              : { right: 12, top: 52 }
          }
        >
          <div className="min-w-0 flex-1 pr-2">
            <span role="status" className="flex items-center gap-2 text-sm font-semibold">
              <span
                aria-hidden
                className={cn(
                  'h-2 w-2 rounded-full',
                  isRecording ? 'bg-red-600 motion-safe:animate-pulse' : 'bg-amber-500'
                )}
              />
              {statusText}
            </span>
            <span className="block max-w-48 truncate text-xs text-muted-foreground">
              {originRef.current?.name}
            </span>
          </div>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-foreground" />
              <span className="text-xs font-medium text-foreground">{submissionStatusText}</span>
            </>
          ) : isConfirmed ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-foreground" />
              <span className="text-xs font-medium text-foreground">Confirmed</span>
            </>
          ) : isRecording ? (
            <>
              <button
                type="button"
                onClick={pauseRecording}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-black transition-colors hover:bg-gray-200"
                aria-label="Pause recording"
              >
                <Pause className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={finishRecording}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-black transition-colors hover:bg-gray-200"
                aria-label="Finish recording"
              >
                <Square className="h-4 w-4" />
              </button>
              <span className="text-xs font-semibold text-foreground">
                {formatDuration(durationSeconds)}
              </span>
              <div aria-hidden className="hidden items-end gap-1 px-1 sm:flex">
                {[8, 12, 16, 10].map((height, index) => (
                  <span
                    key={`${height}-${index}`}
                    className="w-1 rounded-full bg-primary/70 transition-opacity duration-150 motion-reduce:transition-none"
                    style={{
                      height: isWaveTickHigh ? `${height}px` : `${Math.max(6, height - 4)}px`,
                    }}
                  />
                ))}
              </div>
            </>
          ) : isPaused ? (
            <>
              <button
                type="button"
                onClick={() => {
                  if (currentClient && currentClient !== originRef.current?.context.client)
                    setSwitchClientOpen(true);
                  else resumeRecording();
                }}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-black transition-colors hover:bg-gray-200"
                aria-label="Resume recording"
              >
                <Play className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={finishRecording}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-black transition-colors hover:bg-gray-200"
                aria-label="Finish recording"
              >
                <Square className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={discardDraft}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-black transition-colors hover:bg-gray-200"
                aria-label="Discard recording"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <span className="text-xs font-semibold text-foreground">
                {formatDuration(durationSeconds)}
              </span>
            </>
          ) : null}
        </div>
      )}
      {showChatButton && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={openChat}
          className="fixed right-[88px] top-2 z-40 h-8 min-h-0 w-8 min-w-0 rounded-lg bg-popover shadow-sm"
          aria-label="Open chat"
          title="Open chat"
        >
          <MessageSquare className="h-4 w-4" />
        </Button>
      )}
      {renderTrigger
        ? renderTrigger({
            visible: showVoiceRecorder || showActionPanel,
            status: statusText,
            onClick: () => {
              if (isPaused && draftBlob) setIsDraftReviewOpen(true);
              else if (!showActionPanel && canRecord) startRecordingSegment();
            },
            disabled: isStarting || isSubmitting || (!canRecord && !showActionPanel),
            active: showActionPanel,
            label: isRecording
              ? 'Voice note recording'
              : isPaused
                ? 'Voice note paused'
                : 'Start voice note',
            icon: isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isConfirmed ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <Mic className="h-4 w-4" />
            ),
          })
        : showVoiceRecorder && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => {
                if (isRecording || isPaused || isSubmitting || !canRecord) return;
                startRecordingSegment();
              }}
              disabled={!canRecord || isSubmitting}
              className={cn(
                'fixed z-40 rounded-lg bg-popover shadow-sm',
                isMobile
                  ? 'h-11 w-11 border-0 bg-transparent shadow-none'
                  : 'right-12 top-2 h-8 min-h-0 w-8 min-w-0',
                showActionPanel && 'bg-primary text-primary-foreground hover:bg-primary/90'
              )}
              style={
                isMobile ? { right: 64, top: 'calc(6px + env(safe-area-inset-top))' } : undefined
              }
              aria-label={
                isRecording
                  ? 'Voice note recording'
                  : isPaused
                    ? 'Voice note paused'
                    : 'Start voice note'
              }
              title={isRecording ? 'Recording in progress' : 'Record a voice note'}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isConfirmed ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </Button>
          )}

      <Dialog
        open={switchClientOpen}
        onOpenChange={(open) => {
          if (!open) setSwitchClientOpen(false);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Recording paused: different client</DialogTitle>
            <DialogDescription>
              This recording belongs to {originRef.current?.name || 'the original client'}. Finish
              and review it before starting a separate recording for the new client, or return to
              continue.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => {
                const origin = originRef.current;
                if (!origin) return;
                setSwitchClientOpen(false);
                navigate(origin.route);
                lastCheckedClientRef.current = origin.context.client;
                resumeRecording();
              }}
            >
              Return and continue
            </Button>
            <Button
              onClick={() => {
                setSwitchClientOpen(false);
                if (mediaRecorderRef.current?.state === 'inactive') setIsDraftReviewOpen(true);
                else finishRecording();
              }}
            >
              Finish and review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDraftReviewOpen} onOpenChange={setIsDraftReviewOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Review recording draft</DialogTitle>
            <DialogDescription>
              Playback your draft before submitting. You can keep recording as a new segment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {draftAudioUrl ? (
              <audio controls className="w-full" src={draftAudioUrl}>
                <track kind="captions" />
              </audio>
            ) : null}
            <div className="rounded-xl border border-gray-200 p-3 text-xs text-gray-600">
              <div>Duration: {formatDuration(durationSeconds)}</div>
              <div>Page: {(originRef.current?.context ?? recordingContext).page_key}</div>

              <div>Recording for: {originRef.current?.name || 'Voice note'}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={discardDraft} disabled={isSubmitting}>
              Discard
            </Button>
            <Button
              variant="outline"
              onClick={startRecordingSegment}
              disabled={
                isSubmitting ||
                isStarting ||
                (!!currentClient && currentClient !== originRef.current?.context.client)
              }
            >
              Continue recording
            </Button>
            <Button onClick={submitDraft} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {submissionStatusText}
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
